// v88 — one loader for the feature access checks (Creator 360, Live Streaming Intel, Bot Detection).
// A real answer (yes, or a 401/403/404 "no") is kept for the tab. A failed check (server busy, restarting,
// network blip) is retried quietly and never counts as "no": the page waits instead of bouncing, and the
// menu keeps the last real answer this tab had. The server still refuses every request a user may not make.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const DENIED = new Set([401, 403, 404]);
const BACKOFF_MS = [1000, 2000, 4000, 8000, 15000];

export function makeAccess(kind, path, map) {
  const cache = new Map(); // email -> Promise<value>
  const listeners = new Set();
  const memKey = (email) => `kolradar:access:${kind}:${email}`;
  const remembered = (email) => {
    try { const s = sessionStorage.getItem(memKey(email)); return s ? { value: JSON.parse(s) } : null; } catch (_) { return null; }
  };
  const load = (email) => new Promise((resolve) => {
    let tries = 0;
    const go = () => api.get(path).then((r) => map(r.data), (err) => {
      const st = err?.response?.status;
      if (DENIED.has(st)) return map(null);
      throw err;
    }).then((v) => {
      try { sessionStorage.setItem(memKey(email), JSON.stringify(v)); } catch (_) { /* ignore */ }
      resolve(v);
    }, () => { setTimeout(go, BACKOFF_MS[Math.min(tries++, BACKOFF_MS.length - 1)]); });
    go();
  });
  const reset = () => { cache.clear(); listeners.forEach((fn) => fn()); };

  function useAccess() {
    const { user, loading } = useAuth();
    const email = user?.email || "";
    const [state, setState] = useState(() => ({ email, value: (email ? remembered(email)?.value : undefined) ?? map(null), ready: false }));
    const [tick, setTick] = useState(0);
    useEffect(() => {
      const fn = () => setTick((t) => t + 1);
      listeners.add(fn);
      return () => listeners.delete(fn);
    }, []);
    useEffect(() => {
      if (!email) { setState({ email: "", value: map(null), ready: !loading }); return undefined; }
      let alive = true;
      const mem = remembered(email);
      if (mem) setState((s) => (s.email === email && s.ready ? s : { email, value: mem.value, ready: true }));
      if (!cache.has(email)) cache.set(email, load(email));
      cache.get(email).then((value) => { if (alive) setState({ email, value, ready: true }); });
      return () => { alive = false; };
    }, [email, tick, loading]);
    const fresh = state.email === email;
    return { value: fresh ? state.value : map(null), ready: fresh ? (email ? state.ready : !loading) : false };
  }
  return { useAccess, reset };
}
